import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase, Profile } from '../lib/supabase';
import { findInstitutionByName } from '../data/undergraduateInstitutions';
import { institutionLogoCandidateUrls } from '../lib/institutionLogo';
import './AuthPages.css';
import './AccountPage.css';

function AccountInstitutionLogo({ schoolName }: { schoolName: string }) {
  const institution = findInstitutionByName(schoolName);
  const urls = institution ? institutionLogoCandidateUrls(institution.domain) : [];
  const [sourceIndex, setSourceIndex] = useState(0);

  useEffect(() => {
    setSourceIndex(0);
  }, [schoolName]);

  if (!urls.length || sourceIndex >= urls.length) {
    return null;
  }

  return (
    <img
      key={`${institution?.domain ?? schoolName}-${sourceIndex}`}
      className="account-school-logo"
      src={urls[sourceIndex]}
      alt=""
      loading="lazy"
      decoding="async"
      referrerPolicy="no-referrer"
      onError={() => setSourceIndex((i) => i + 1)}
    />
  );
}

const AccountPage: React.FC = () => {
  const { user, signOut } = useAuth();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [fullName, setFullName] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const loadProfile = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const { data, error } = await supabase.from('profiles').select('*').eq('user_id', user.id).maybeSingle();
      if (error) throw error;
      if (data) {
        setProfile(data as Profile);
        setFullName(data.full_name || '');
      } else {
        setProfile(null);
        setFullName('');
      }
    } catch (e) {
      console.error(e);
      setMessage({ type: 'error', text: 'Could not load your profile. Check Supabase RLS and the profiles table.' });
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const handleSaveName = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.id) return;
    setSaving(true);
    setMessage(null);
    try {
      const payload: Partial<Profile> & { user_id: string } = {
        user_id: user.id,
        full_name: fullName.trim() || undefined
      };
      if (profile?.id) {
        payload.id = profile.id;
      }
      const { error } = await supabase.from('profiles').upsert(payload, { onConflict: 'user_id' });
      if (error) throw error;
      setMessage({ type: 'success', text: 'Display name saved.' });
      await loadProfile();
    } catch (err) {
      console.error(err);
      setMessage({ type: 'error', text: 'Could not save. Ensure the profiles table exists and RLS allows your user.' });
    } finally {
      setSaving(false);
    }
  };

  const handleSignOut = async () => {
    await signOut();
  };

  return (
    <div className="auth-page account-page-wrap">
      <div className="auth-card account-card-wide">
        <h1 className="auth-title">My Account</h1>
        <p className="auth-subtitle">
          Signed in as <strong>{user?.email}</strong>
        </p>

        {loading ? (
          <p className="auth-loading-text">Loading profile…</p>
        ) : (
          <>
            {message && (
              <div className={`auth-message ${message.type === 'error' ? 'error' : 'success'}`} role="status">
                {message.text}
              </div>
            )}

            <form className="auth-form account-inline-form" onSubmit={handleSaveName}>
              <div className="auth-field">
                <label htmlFor="account-full-name">Display name (optional)</label>
                <input
                  id="account-full-name"
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Your name"
                  autoComplete="name"
                />
              </div>
              <button type="submit" className="auth-submit" disabled={saving}>
                {saving ? 'Saving…' : 'Save display name'}
              </button>
            </form>

            <div className="account-divider" />

            <h2 className="account-section-title">MBA profile</h2>
            <p className="account-section-copy">
              Build your full deferred MBA profile—school, scores, targets—in the Profile Builder. Your data is stored
              under your account when you&apos;re signed in.
            </p>
            <Link to="/profile-builder" className="account-cta-link">
              Open Profile Builder →
            </Link>

            {profile?.academic_institution && (
              <dl className="account-preview">
                <dt>School</dt>
                <dd className="account-preview-school">
                  <AccountInstitutionLogo schoolName={profile.academic_institution} />
                  <span>{profile.academic_institution}</span>
                </dd>
                {profile.major_minor && (
                  <>
                    <dt>Major</dt>
                    <dd>{profile.major_minor}</dd>
                  </>
                )}
              </dl>
            )}

            <div className="account-actions">
              <button type="button" className="account-sign-out" onClick={handleSignOut}>
                Log out
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AccountPage;
